import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { tzHeaders } from '../utils/requestHeaders';
import {
    PosDashboardSummary, PosDayEndReport, PosDayEndReportFilters, PosSale, PosSaleListFilters,
    PosSaleSummary, PosSettings, PosStoreAssignment, PosTerminal, PosTerminalRegisterResponse, SpringPage,
} from '../interfaces/pos.interface';

export interface PosAssignableUser {
    id: string;
    username: string;
}

interface RawPosStoreAssignment {
    id: string;
    institutionId: string;
    storeId: string;
    userId: string;
    isActive: boolean;
    userName: string | null;
}

// A blank filter must never reach the wire: the backend parses `storeId=` as a malformed UUID and
// answers 400 "Invalid value for parameter: storeId".
const qs = (params: Record<string, string | number | undefined>): string =>
    Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
        .join('&');

export const PosService = {
    getSettings: async (): Promise<PosSettings> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.POS_SETTINGS.BY_INSTITUTION(institutionId));
        return response.data;
    },

    saveSettings: async (settings: PosSettings): Promise<PosSettings> => {
        const response = await axios.put(ENDPOINTS.POS_SETTINGS.SAVE, settings);
        return response.data;
    },

    listTerminals: async (storeId: string): Promise<PosTerminal[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(`${ENDPOINTS.POS_TERMINAL.LIST}?${qs({ institutionId, storeId })}`);
        return response.data;
    },

    registerTerminal: async (
        storeId: string, terminalCode: string, label: string,
    ): Promise<PosTerminalRegisterResponse> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.post(ENDPOINTS.POS_TERMINAL.REGISTER, {
            institutionId, storeId, terminalCode, label,
        });
        return response.data;
    },

    renameTerminal: async (id: string, label: string): Promise<PosTerminal> => {
        const response = await axios.put(ENDPOINTS.POS_TERMINAL.RENAME(id), { label });
        return response.data;
    },

    revokeTerminal: async (id: string): Promise<void> => {
        await axios.post(ENDPOINTS.POS_TERMINAL.REVOKE(id));
    },

    // storeId is deliberately NOT part of this request -- PosCashierPinSetRequest has no such
    // field and PosCashierPin is keyed on (institutionId, userId) only (review F2). A cashier's
    // PIN is one value across every store they are assigned to; sending storeId here would lie
    // about that to the caller, since the backend silently ignores it.
    setCashierPin: async (userId: string, pin: string): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.post(ENDPOINTS.POS_CASHIER_PIN.SET, { institutionId, userId, pin });
    },

    // The backend fills `userName` (username) on each row, so a StoreManager -- who cannot call
    // the InstitutionAdmin-only user directory -- still sees names, not UUIDs. Falls back to the
    // userId only if the server could not resolve a name.
    listStoreAssignments: async (storeId: string): Promise<PosStoreAssignment[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get<RawPosStoreAssignment[]>(
            `${ENDPOINTS.POS_STORE_ASSIGNMENT.LIST}?${qs({ institutionId, storeId })}`,
        );
        return response.data.map(a => ({ ...a, userName: a.userName ?? a.userId }));
    },

    // Active members of the institution the caller may assign (InstitutionAdmin/StoreAdmin/
    // StoreManager), minimal {id, username}. Replaces the InstitutionAdmin-only user directory.
    listAssignableUsers: async (): Promise<PosAssignableUser[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get<PosAssignableUser[]>(
            `${ENDPOINTS.POS_STORE_ASSIGNMENT.ASSIGNABLE_USERS}?${qs({ institutionId })}`,
        );
        return response.data;
    },

    assignCashier: async (storeId: string, userId: string): Promise<void> => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.post(ENDPOINTS.POS_STORE_ASSIGNMENT.CREATE, { institutionId, storeId, userId });
    },

    deactivateStoreAssignment: async (id: string): Promise<void> => {
        await axios.post(ENDPOINTS.POS_STORE_ASSIGNMENT.DEACTIVATE(id));
    },

    reactivateStoreAssignment: async (id: string): Promise<void> => {
        await axios.post(ENDPOINTS.POS_STORE_ASSIGNMENT.REACTIVATE(id));
    },

    listSales: async (filters: PosSaleListFilters): Promise<SpringPage<PosSaleSummary>> => {
        const institutionId = AuthService.getSelectedInstitution();
        const query = qs({ institutionId, ...filters });
        const response = await axios.get(`${ENDPOINTS.POS_SALE.LIST}?${query}`, { headers: tzHeaders() });
        return response.data;
    },

    getSale: async (id: string): Promise<PosSale> => {
        const response = await axios.get(ENDPOINTS.POS_SALE.DETAIL(id), { headers: tzHeaders() });
        return response.data;
    },

    // A blank reason must reach the wire as ABSENT, not as ''. PosSaleQueryService.voidSale's
    // fallback ("Void of POS sale " + receiptNo) only fires when reason is null, and an empty
    // string is not null -- it would silently defeat the fallback on the common path, since the
    // reason field is optional (review F2). `undefined` is dropped by JSON.stringify, so the body
    // has no `reason` key and Jackson deserialises the record field as null.
    voidSale: async (id: string, reason?: string): Promise<PosSale> => {
        const response = await axios.post(ENDPOINTS.POS_SALE.VOID(id), { reason: reason || undefined });
        return response.data;
    },

    listUnposted: async (page: number, size: number): Promise<SpringPage<PosSaleSummary>> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(`${ENDPOINTS.POS_SALE.UNPOSTED}?${qs({ institutionId, page, size })}`,
            { headers: tzHeaders() });
        return response.data;
    },

    postNow: async (id: string): Promise<PosSale> => {
        const response = await axios.post(ENDPOINTS.POS_SALE.POST_NOW(id));
        return response.data;
    },

    // The X-Timezone header is not optional here. Corrected (final-review F3): the backend does
    // NOT window the day itself in the caller's zone -- PosReportService.getDayEndReport passes
    // fromSoldAt/toSoldAt straight through, verbatim, to EVERY query including byHour. The zone
    // affects ONLY how byHour LABELS its buckets (PosReportService.java:106-116 passes
    // zone.getId() into that query's date_trunc and nowhere else); its WHERE clause takes the
    // same verbatim window as the rest. The day window itself is entirely this client's own
    // doing -- the caller
    // (PosDayEndReport.tsx / PosSaleList.tsx) converts the picked calendar date into UTC instants
    // via toDayStartIso/toDayEndIso BEFORE calling this method, and fromSoldAt/toSoldAt below are
    // passed straight through, verbatim, to the server. A missing X-Timezone header would not
    // shift the window at all -- it would only mislabel the byHour buckets against the wrong
    // wall-clock hours. Do not treat this call as already zone-safe: the client-side
    // date-to-instant conversion upstream of this call is still load-bearing for the day boundary
    // itself. See tzHeaders()'s own doc for the header itself.
    getDayEndReport: async (filters: PosDayEndReportFilters): Promise<PosDayEndReport> => {
        const institutionId = AuthService.getSelectedInstitution();
        const query = qs({ institutionId, ...filters });
        const response = await axios.get(`${ENDPOINTS.POS_REPORTS.DAY_END}?${query}`, { headers: tzHeaders() });
        return response.data;
    },

    // Same X-Timezone requirement as getDayEndReport above -- "today" is resolved server-side
    // from this header, over sold_at. A missing header would silently show a UTC+6 tenant's
    // early-morning tile the previous day's figures.
    getDashboardSummary: async (storeId?: string): Promise<PosDashboardSummary> => {
        const institutionId = AuthService.getSelectedInstitution();
        const query = qs({ institutionId, storeId });
        const response = await axios.get(`${ENDPOINTS.POS_REPORTS.DASHBOARD}?${query}`, { headers: tzHeaders() });
        return response.data;
    },
};
